/**
 * TreatX Admin - Treatment Management Page
 * Full CRUD for treatments with complete content parity with public pages
 * Includes: Overview, Benefits, Risks, What to Expect, Who Is This For, Why Abroad, FAQ, Pricing, Images
 * P2.3b: Full i18n coverage
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTreatXAdminAuth } from '../../context/TreatXAdminAuthContext';
import { useTreatmentManagement, TREATMENT_CATEGORIES } from '../../context/TreatmentManagementContext';
import { getLocalizedField } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Shield, LogOut, Plus, Search, Edit2, Trash2, ArrowLeft,
  DollarSign, Building2, Eye, EyeOff, Save, X, Image as ImageIcon,
  Clock, HelpCircle, ChevronUp, ChevronDown, Upload, Users, FileText,
  CheckCircle, AlertCircle, Heart, Globe, GripVertical
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';
import { ImageUploadField, GalleryUploader } from '../../components/ImageUploadField';

// Alias for JSX readability
const TreatmentImageUploader = (props) => <ImageUploadField assetType="treatment_image" {...props} />;
const TreatmentGalleryUploader = (props) => <GalleryUploader assetType="treatment_image" {...props} />;

// Gender Groups
const GENDER_GROUPS = [
  { id: 'women', labelKey: 'provider.treatmentMgmt.women', icon: '👩' },
  { id: 'men', labelKey: 'provider.treatmentMgmt.men', icon: '👨' },
  { id: 'non-binary', labelKey: 'provider.treatmentMgmt.nonBinary', icon: '🧑' },
  { id: 'prefer-not-to-say', labelKey: 'provider.treatmentMgmt.preferNotToSay', icon: '👤' }
];

// Bullet List Editor Component
const BulletListEditor = ({ items, onChange, placeholder, icon: Icon, iconColor = 'text-gray-500' }) => {
  const { t } = useTranslation();
  const addItem = () => {
    onChange([...items, '']);
  };

  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, value) => {
    const updated = [...items];
    updated[index] = value;
    onChange(updated);
  };

  const moveItem = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    const updated = [...items];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-start gap-2 group">
          <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => moveItem(index, -1)}
              disabled={index === 0}
              className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30"
            >
              <ChevronUp className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => moveItem(index, 1)}
              disabled={index === items.length - 1}
              className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30"
            >
              <ChevronDown className="h-3 w-3" />
            </button>
          </div>
          <Icon className={`h-5 w-5 mt-2.5 flex-shrink-0 ${iconColor}`} />
          <Input
            value={item}
            onChange={(e) => updateItem(index, e.target.value)}
            placeholder={placeholder}
            className="flex-1"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => removeItem(index)}
            className="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addItem} className="mt-2">
        <Plus className="h-4 w-4 mr-1" />
        {t('provider.treatmentMgmt.addItem')}
      </Button>
    </div>
  );
};

// Treatment Edit/Create Modal
const TreatmentEditModal = ({ treatment, onSave, onClose, categories }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    // Basic Info
    name: treatment?.name || '',
    slug: treatment?.slug || '',
    category: treatment?.category || 'dental',
    shortDescription: treatment?.shortDescription || '',
    genderGroups: treatment?.genderGroups || ['women', 'men', 'non-binary', 'prefer-not-to-say'],
    status: treatment?.status || 'draft',
    
    // Medical Content
    overview: treatment?.description?.overview || treatment?.overview || '',
    benefits: treatment?.description?.benefits || treatment?.benefits || [''],
    risks: treatment?.description?.risks || treatment?.risks || [''],
    expectations: treatment?.description?.expectations || treatment?.expectations || '',
    whoIsThisFor: treatment?.whoIsThisFor || [''],
    
    // Quick Info
    procedureTime: treatment?.procedureDuration || treatment?.procedureTime || '',
    recoveryTime: treatment?.recoveryTime || '',
    stayDuration: treatment?.stayDuration || '',
    
    // Patient Motivation
    whyAbroad: treatment?.whyAbroad || [''],
    faqs: treatment?.faqs || [{ question: '', answer: '' }],
    
    // Pricing
    priceMode: treatment?.priceMode || 'auto',
    manualPriceMin: treatment?.manualPriceMin || '',
    manualPriceMax: treatment?.manualPriceMax || '',
    calculatedPriceMin: treatment?.priceRange?.min || treatment?.calculatedPriceMin || 0,
    calculatedPriceMax: treatment?.priceRange?.max || treatment?.calculatedPriceMax || 0,
    currency: treatment?.currency || 'EUR',
    priceNote: treatment?.priceRange?.note || '',
    
    // Images
    images: treatment?.images || [],
    coverImage: treatment?.image || treatment?.coverImage || '',
    media_id: treatment?.media_id || null,
    
    // Meta
    clinicsOffering: treatment?.clinicsOffering || 0,

    // SEO
    seoTitle: treatment?.seoTitle || '',
    seoDescription: treatment?.seoDescription || '',
    seoKeywords: treatment?.seoKeywords || '',
    canonicalUrl: treatment?.canonicalUrl || '',
    seoImage: treatment?.seoImage || '',
    slugOverride: treatment?.slugOverride || '',
  });

  const [newImageUrl, setNewImageUrl] = useState('');

  // Handle name change and auto-generate slug
  const handleNameChange = (name) => {
    if (!treatment) {
      setFormData(prev => ({
        ...prev,
        name,
        slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      }));
    } else {
      setFormData(prev => ({ ...prev, name }));
    }
  };

  const handleGenderToggle = (genderId) => {
    setFormData(prev => ({
      ...prev,
      genderGroups: prev.genderGroups.includes(genderId)
        ? prev.genderGroups.filter(g => g !== genderId)
        : [...prev.genderGroups, genderId]
    }));
  };

  // FAQ Handlers
  const addFaq = () => {
    setFormData(prev => ({
      ...prev,
      faqs: [...prev.faqs, { question: '', answer: '' }]
    }));
  };

  const removeFaq = (index) => {
    setFormData(prev => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== index)
    }));
  };

  const updateFaq = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      faqs: prev.faqs.map((faq, i) => i === index ? { ...faq, [field]: value } : faq)
    }));
  };

  const moveFaq = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= formData.faqs.length) return;
    setFormData(prev => {
      const updated = [...prev.faqs];
      [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
      return { ...prev, faqs: updated };
    });
  };

  // Image Handlers
  const addImage = () => {
    if (newImageUrl.trim()) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, { url: newImageUrl.trim(), caption: '' }],
        coverImage: prev.coverImage || newImageUrl.trim()
      }));
      setNewImageUrl('');
    }
  };

  const removeImage = (index) => {
    setFormData(prev => {
      const newImages = prev.images.filter((_, i) => i !== index);
      return {
        ...prev,
        images: newImages,
        coverImage: newImages.length > 0 ? newImages[0].url : ''
      };
    });
  };

  const setCoverImage = (url) => {
    setFormData(prev => ({ ...prev, coverImage: url }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast({ title: t('provider.treatmentMgmt.errorTitle'), description: t('provider.treatmentMgmt.nameRequired'), variant: 'destructive' });
      return;
    }
    if (formData.genderGroups.length === 0) {
      toast({ title: t('provider.treatmentMgmt.errorTitle'), description: t('provider.treatmentMgmt.genderRequired'), variant: 'destructive' });
      return;
    }
    
    // Transform data for storage
    const saveData = {
      ...formData,
      description: {
        overview: formData.overview,
        benefits: formData.benefits.filter(b => b.trim()),
        risks: formData.risks.filter(r => r.trim()),
        expectations: formData.expectations
      },
      whoIsThisFor: formData.whoIsThisFor.filter(w => w.trim()),
      whyAbroad: formData.whyAbroad.filter(w => w.trim()),
      faqs: formData.faqs.filter(f => f.question.trim() && f.answer.trim()),
      priceRange: {
        min: formData.priceMode === 'manual' && formData.manualPriceMin 
          ? Number(formData.manualPriceMin) 
          : formData.calculatedPriceMin,
        max: formData.priceMode === 'manual' && formData.manualPriceMax 
          ? Number(formData.manualPriceMax) 
          : formData.calculatedPriceMax,
        currency: formData.currency,
        note: formData.priceNote
      },
      image: formData.coverImage,
      media_id: formData.media_id,
      procedureDuration: formData.procedureTime,
      isActive: formData.status === 'published',
      // SEO fields
      seoTitle: formData.seoTitle,
      seoDescription: formData.seoDescription,
      seoKeywords: formData.seoKeywords,
      canonicalUrl: formData.canonicalUrl,
      seoImage: formData.seoImage,
      slugOverride: formData.slugOverride,
    };
    
    onSave(saveData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">
                {treatment ? t('provider.treatmentMgmt.editTreatment') : t('provider.treatmentMgmt.addNewTreatment')}
              </h2>
              <p className="text-purple-200 text-sm mt-1">
                {treatment ? t('provider.treatmentMgmt.editingLabel', { name: getLocalizedField(treatment.name, 'en') }) : t('provider.treatmentMgmt.createNewSubtitle')}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Tabs - 5 Tabs as per UX Recommendation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="flex-shrink-0 mx-6 mt-4 grid grid-cols-6">
            <TabsTrigger value="basic" className="text-xs sm:text-sm">{t('provider.treatmentMgmt.tabBasicInfo')}</TabsTrigger>
            <TabsTrigger value="medical" className="text-xs sm:text-sm">{t('provider.treatmentMgmt.tabMedicalContent')}</TabsTrigger>
            <TabsTrigger value="motivation" className="text-xs sm:text-sm">{t('provider.treatmentMgmt.tabPatientMotivation')}</TabsTrigger>
            <TabsTrigger value="pricing" className="text-xs sm:text-sm">{t('provider.treatmentMgmt.tabPricing')}</TabsTrigger>
            <TabsTrigger value="media" className="text-xs sm:text-sm">{t('provider.treatmentMgmt.tabMedia')}</TabsTrigger>
            <TabsTrigger value="seo" className="text-xs sm:text-sm">{t('provider.treatmentMgmt.tabSeo')}</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto p-6">
            {/* TAB 1: Basic Info */}
            <TabsContent value="basic" className="space-y-6 mt-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>{t('provider.treatmentMgmt.treatmentNameLabel')} *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder={t('provider.treatmentMgmt.treatmentNamePh')}
                    required
                  />
                </div>

                <div>
                  <Label>{t('provider.treatmentMgmt.urlSlug')}</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="hair-transplant-fue-dhi"
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('provider.treatmentMgmt.autoGeneratedFromName')}</p>
                </div>

                <div>
                  <Label>{t('provider.treatmentMgmt.categoryLabel')} *</Label>
                  <select
                    className="w-full border rounded-lg p-2 h-10"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {Object.entries(categories).map(([key, cat]) => (
                      <option key={key} value={cat.id}>{cat.icon} {cat.label}</option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2">
                  <Label>{t('provider.treatmentMgmt.shortDescriptionLabel')} * ({t('provider.treatmentMgmt.shortDescriptionScope')})</Label>
                  <Input
                    value={formData.shortDescription}
                    onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                    placeholder={t('provider.treatmentMgmt.shortDescPh')}
                    maxLength={150}
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('provider.treatmentMgmt.charCount', { count: formData.shortDescription.length, max: 150 })}</p>
                </div>
              </div>

              {/* Quick Info */}
              <div className="border-t pt-4">
                <Label className="mb-3 block text-base font-semibold">{t('provider.treatmentMgmt.quickInfo')}</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm">{t('provider.treatmentMgmt.procedureDuration')}</Label>
                    <Input
                      value={formData.procedureTime}
                      onChange={(e) => setFormData({ ...formData, procedureTime: e.target.value })}
                      placeholder={t('provider.treatmentMgmt.procDurationPh')}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('provider.treatmentMgmt.recoveryTime')}</Label>
                    <Input
                      value={formData.recoveryTime}
                      onChange={(e) => setFormData({ ...formData, recoveryTime: e.target.value })}
                      placeholder={t('provider.treatmentMgmt.recoveryTimePh')}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('provider.treatmentMgmt.recommendedStay')}</Label>
                    <Input
                      value={formData.stayDuration}
                      onChange={(e) => setFormData({ ...formData, stayDuration: e.target.value })}
                      placeholder={t('provider.treatmentMgmt.stayDurationPh')}
                    />
                  </div>
                </div>
              </div>

              {/* Gender Groups */}
              <div className="border-t pt-4">
                <Label className="mb-3 block">{t('provider.treatmentMgmt.genderClassification')} *</Label>
                <p className="text-sm text-gray-500 mb-3">{t('provider.treatmentMgmt.genderClassificationHelp')}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {GENDER_GROUPS.map(gender => (
                    <button
                      key={gender.id}
                      type="button"
                      onClick={() => handleGenderToggle(gender.id)}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        formData.genderGroups.includes(gender.id)
                          ? 'border-purple-600 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">{gender.icon}</div>
                      <div className="text-sm font-medium">{t(gender.labelKey)}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Status */}
              <div className="border-t pt-4">
                <Label className="mb-3 block">{t('provider.treatmentMgmt.publicationStatus')}</Label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, status: 'draft' })}
                    className={`flex-1 p-4 rounded-lg border-2 ${
                      formData.status === 'draft' 
                        ? 'border-amber-500 bg-amber-50' 
                        : 'border-gray-200'
                    }`}
                  >
                    <EyeOff className="h-5 w-5 mx-auto mb-2 text-amber-600" />
                    <p className="font-medium">{t('provider.treatmentMgmt.draft')}</p>
                    <p className="text-xs text-gray-500">{t('provider.treatmentMgmt.draftHelp')}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, status: 'published' })}
                    className={`flex-1 p-4 rounded-lg border-2 ${
                      formData.status === 'published' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200'
                    }`}
                  >
                    <Eye className="h-5 w-5 mx-auto mb-2 text-green-600" />
                    <p className="font-medium">{t('provider.treatmentMgmt.published')}</p>
                    <p className="text-xs text-gray-500">{t('provider.treatmentMgmt.publishedHelp')}</p>
                  </button>
                </div>
              </div>
            </TabsContent>

            {/* TAB 2: Medical Content */}
            <TabsContent value="medical" className="space-y-6 mt-0">
              {/* Overview */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <Label className="text-base font-semibold">{t('provider.treatmentMgmt.overview')}</Label>
                </div>
                <p className="text-sm text-gray-500 mb-2">
                  {t('provider.treatmentMgmt.overviewHelp')}
                </p>
                <Textarea
                  value={formData.overview}
                  onChange={(e) => setFormData({ ...formData, overview: e.target.value })}
                  placeholder={t('provider.treatmentMgmt.overviewPh')}
                  rows={5}
                  className="resize-y"
                />
              </div>

              {/* Benefits */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <Label className="text-base font-semibold">{t('provider.treatmentMgmt.benefits')}</Label>
                </div>
                <p className="text-sm text-gray-500 mb-3">
                  {t('provider.treatmentMgmt.benefitsHelp')}
                </p>
                <BulletListEditor
                  items={formData.benefits}
                  onChange={(items) => setFormData({ ...formData, benefits: items })}
                  placeholder={t('provider.treatmentMgmt.benefitsPh')}
                  icon={CheckCircle}
                  iconColor="text-green-600"
                />
              </div>

              {/* Risks & Considerations */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                  <Label className="text-base font-semibold">{t('provider.treatmentMgmt.risks')}</Label>
                </div>
                <p className="text-sm text-gray-500 mb-3">
                  {t('provider.treatmentMgmt.risksHelp')}
                </p>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
                  <p className="text-sm text-orange-800">
                    ⚠️ {t('provider.treatmentMgmt.risksWarning')}
                  </p>
                </div>
                <BulletListEditor
                  items={formData.risks}
                  onChange={(items) => setFormData({ ...formData, risks: items })}
                  placeholder={t('provider.treatmentMgmt.risksPh')}
                  icon={AlertCircle}
                  iconColor="text-orange-600"
                />
              </div>

              {/* What to Expect */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-5 w-5 text-blue-600" />
                  <Label className="text-base font-semibold">{t('provider.treatmentMgmt.whatToExpect')}</Label>
                </div>
                <p className="text-sm text-gray-500 mb-2">
                  {t('provider.treatmentMgmt.whatToExpectHelp')}
                </p>
                <Textarea
                  value={formData.expectations}
                  onChange={(e) => setFormData({ ...formData, expectations: e.target.value })}
                  placeholder={t('provider.treatmentMgmt.whatToExpectPh')}
                  rows={4}
                  className="resize-y"
                />
              </div>

              {/* Who Is This For */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Heart className="h-5 w-5 text-pink-600" />
                  <Label className="text-base font-semibold">{t('provider.treatmentMgmt.whoIsThisFor')}</Label>
                </div>
                <p className="text-sm text-gray-500 mb-3">
                  {t('provider.treatmentMgmt.whoIsThisForHelp')}
                </p>
                <BulletListEditor
                  items={formData.whoIsThisFor}
                  onChange={(items) => setFormData({ ...formData, whoIsThisFor: items })}
                  placeholder={t('provider.treatmentMgmt.whoIsThisForPh')}
                  icon={Heart}
                  iconColor="text-pink-600"
                />
              </div>
            </TabsContent>

            {/* TAB 3: Patient Motivation */}
            <TabsContent value="motivation" className="space-y-6 mt-0">
              {/* Why Choose This Treatment Abroad */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="h-5 w-5 text-blue-600" />
                  <Label className="text-base font-semibold">{t('provider.treatmentMgmt.whyAbroad')}</Label>
                </div>
                <p className="text-sm text-gray-500 mb-3">
                  {t('provider.treatmentMgmt.whyAbroadHelp')}
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <p className="text-sm text-blue-800">
                    💡 {t('provider.treatmentMgmt.whyAbroadInfo')}
                  </p>
                </div>
                <BulletListEditor
                  items={formData.whyAbroad}
                  onChange={(items) => setFormData({ ...formData, whyAbroad: items })}
                  placeholder={t('provider.treatmentMgmt.whyAbroadPh')}
                  icon={Globe}
                  iconColor="text-blue-600"
                />
              </div>

              {/* FAQ Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <HelpCircle className="h-5 w-5 text-purple-600" />
                      <Label className="text-base font-semibold">{t('provider.treatmentMgmt.faq')}</Label>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{t('provider.treatmentMgmt.faqHelp')}</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addFaq}>
                    <Plus className="h-4 w-4 mr-1" />
                    {t('provider.treatmentMgmt.addFaq')}
                  </Button>
                </div>

                <div className="space-y-4">
                  {formData.faqs.map((faq, index) => (
                    <Card key={index} className="bg-slate-50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex flex-col gap-1">
                            <button
                              type="button"
                              onClick={() => moveFaq(index, -1)}
                              disabled={index === 0}
                              className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                            >
                              <ChevronUp className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveFaq(index, 1)}
                              disabled={index === formData.faqs.length - 1}
                              className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                            >
                              <ChevronDown className="h-4 w-4" />
                            </button>
                          </div>
                          <HelpCircle className="h-5 w-5 text-purple-600 mt-1 flex-shrink-0" />
                          <div className="flex-1 space-y-3">
                            <div>
                              <Label className="text-xs">{t('provider.treatmentMgmt.questionN', { index: index + 1 })}</Label>
                              <Input
                                value={faq.question}
                                onChange={(e) => updateFaq(index, 'question', e.target.value)}
                                placeholder={t('provider.treatmentMgmt.faqQuestionPh')}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">{t('provider.treatmentMgmt.faqAnswer')}</Label>
                              <Textarea
                                value={faq.answer}
                                onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                                placeholder={t('provider.treatmentMgmt.faqAnswerPh')}
                                rows={3}
                              />
                            </div>
                          </div>
                          {formData.faqs.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFaq(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* TAB 4: Pricing */}
            <TabsContent value="pricing" className="space-y-6 mt-0">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <DollarSign className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900">{t('provider.treatmentMgmt.priceRangeConfig')}</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      {t('provider.treatmentMgmt.priceRangeConfigHelp')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Auto Calculated Info */}
              <Card className="bg-slate-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-gray-600" />
                      <span className="font-medium">{t('provider.treatmentMgmt.autoCalculatedRange')}</span>
                    </div>
                    <Badge className="bg-green-100 text-green-700">{t('provider.treatmentMgmt.fromClinics', { count: formData.clinicsOffering || 0 })}</Badge>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    €{(formData.calculatedPriceMin || 0).toLocaleString()} - €{(formData.calculatedPriceMax || 0).toLocaleString()}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t('provider.treatmentMgmt.basedOnActive')}</p>
                </CardContent>
              </Card>

              {/* Price Mode Selection */}
              <div className="space-y-3">
                <div 
                  onClick={() => setFormData({ ...formData, priceMode: 'auto' })}
                  className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.priceMode === 'auto' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    checked={formData.priceMode === 'auto'}
                    onChange={() => setFormData({ ...formData, priceMode: 'auto' })}
                    className="h-4 w-4 text-green-600"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{t('provider.treatmentMgmt.useAutomaticRange')}</span>
                      <Badge className="bg-green-100 text-green-700">{t('provider.treatmentMgmt.recommended')}</Badge>
                    </div>
                    <p className="text-sm text-gray-600">{t('provider.treatmentMgmt.priceRangeCalcHelp')}</p>
                  </div>
                  {formData.priceMode === 'auto' && (
                    <Badge className="bg-green-600 text-white">{t('provider.treatmentMgmt.active')}</Badge>
                  )}
                </div>

                <div 
                  onClick={() => setFormData({ ...formData, priceMode: 'manual' })}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.priceMode === 'manual' 
                      ? 'border-amber-500 bg-amber-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <input
                      type="radio"
                      checked={formData.priceMode === 'manual'}
                      onChange={() => setFormData({ ...formData, priceMode: 'manual' })}
                      className="h-4 w-4 text-amber-600"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{t('provider.treatmentMgmt.manualOverride')}</span>
                        {formData.priceMode === 'manual' && (
                          <Badge className="bg-amber-600 text-white">{t('provider.treatmentMgmt.active')}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{t('provider.treatmentMgmt.setCustomPriceHelp')}</p>
                    </div>
                  </div>

                  {formData.priceMode === 'manual' && (
                    <div className="grid grid-cols-2 gap-4 mt-4 ml-8">
                      <div>
                        <Label>{t('provider.treatmentMgmt.minPrice')}</Label>
                        <Input
                          type="number"
                          value={formData.manualPriceMin}
                          onChange={(e) => setFormData({ ...formData, manualPriceMin: e.target.value })}
                          placeholder="1000"
                        />
                      </div>
                      <div>
                        <Label>{t('provider.treatmentMgmt.maxPrice')}</Label>
                        <Input
                          type="number"
                          value={formData.manualPriceMax}
                          onChange={(e) => setFormData({ ...formData, manualPriceMax: e.target.value })}
                          placeholder="5000"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Price Note */}
              <div>
                <Label>{t('provider.treatmentMgmt.priceNote')}</Label>
                <Input
                  value={formData.priceNote}
                  onChange={(e) => setFormData({ ...formData, priceNote: e.target.value })}
                  placeholder={t('provider.treatmentMgmt.priceNotePh')}
                />
                <p className="text-xs text-gray-500 mt-1">{t('provider.treatmentMgmt.displayedAlongsidePrice')}</p>
              </div>

              {/* Display Preview */}
              <Card className="border-2 border-purple-200 bg-purple-50">
                <CardContent className="p-4">
                  <h4 className="font-medium text-purple-900 mb-2">{t('provider.treatmentMgmt.publicDisplayPreview')}</h4>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-purple-600" />
                    <span className="text-xl font-bold text-purple-900">
                      €{(formData.priceMode === 'manual' && formData.manualPriceMin 
                        ? formData.manualPriceMin 
                        : formData.calculatedPriceMin || 0).toLocaleString()} 
                      {' - '}
                      €{(formData.priceMode === 'manual' && formData.manualPriceMax 
                        ? formData.manualPriceMax 
                        : formData.calculatedPriceMax || 0).toLocaleString()}
                    </span>
                    <Badge className={formData.priceMode === 'auto' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                      {formData.priceMode === 'auto' ? t('provider.treatmentMgmt.autoBadge') : t('provider.treatmentMgmt.overrideBadge')}
                    </Badge>
                  </div>
                  {formData.priceNote && (
                    <p className="text-sm text-purple-700 mt-1">{formData.priceNote}</p>
                  )}
                  <p className="text-xs text-purple-600 mt-2">
                    {formData.priceMode === 'auto' 
                      ? t('provider.treatmentMgmt.pricesBasedOnVerified')
                      : t('provider.treatmentMgmt.customPriceRange')}
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB 5: Media */}
            <TabsContent value="media" className="space-y-6 mt-0">
              {/* Cover Image Upload */}
              <div>
                <Label className="text-base font-semibold mb-2 block">{t('provider.treatmentMgmt.coverImage')}</Label>
                <p className="text-sm text-gray-500 mb-3">
                  {t('provider.treatmentMgmt.coverImageHelp')}
                </p>
                <TreatmentImageUploader
                  currentImage={formData.coverImage}
                  mediaId={formData.media_id}
                  onUploadComplete={(asset) => {
                    setFormData(prev => ({
                      ...prev,
                      coverImage: asset.public_url || asset.signed_url || prev.coverImage,
                      media_id: asset.id,
                    }));
                  }}
                  onClear={() => {
                    setFormData(prev => ({ ...prev, coverImage: '', media_id: null }));
                  }}
                />
              </div>

              {/* Additional Images (Gallery) */}
              <div className="border-t pt-4">
                <Label className="text-base font-semibold mb-2 block">{t('provider.treatmentMgmt.additionalGallery')}</Label>
                <p className="text-sm text-gray-500 mb-3">
                  {t('provider.treatmentMgmt.galleryHelp')}
                </p>
                <TreatmentGalleryUploader
                  images={formData.images}
                  coverImage={formData.coverImage}
                  onImagesChange={(imgs) => setFormData(prev => ({ ...prev, images: imgs }))}
                  onSetCover={(url) => setFormData(prev => ({ ...prev, coverImage: url }))}
                />
              </div>
            </TabsContent>

            {/* SEO Settings Tab */}
            <TabsContent value="seo" className="space-y-6 mt-0">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  {t('provider.treatmentMgmt.seoIntro')}
                </p>
              </div>

              <div className="grid gap-4">
                <div>
                  <Label className="text-sm font-medium">{t('provider.treatmentMgmt.seoTitle')}</Label>
                  <p className="text-xs text-gray-500 mb-1">{t('provider.treatmentMgmt.seoTitleHelp')}</p>
                  <Input
                    value={formData.seoTitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, seoTitle: e.target.value }))}
                    placeholder={formData.name ? `${formData.name} | TreatX` : t('provider.treatmentMgmt.seoTitlePh')}
                    data-testid="seo-title-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">{t('provider.treatmentMgmt.charCount', { count: formData.seoTitle.length, max: 60 })}</p>
                </div>

                <div>
                  <Label className="text-sm font-medium">{t('provider.treatmentMgmt.seoDescription')}</Label>
                  <p className="text-xs text-gray-500 mb-1">{t('provider.treatmentMgmt.seoDescHelp')}</p>
                  <textarea
                    className="w-full border rounded-md p-2 text-sm min-h-[80px] resize-y"
                    value={formData.seoDescription}
                    onChange={(e) => setFormData(prev => ({ ...prev, seoDescription: e.target.value }))}
                    placeholder={formData.shortDescription || t('provider.treatmentMgmt.seoDescPh')}
                    data-testid="seo-description-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">{t('provider.treatmentMgmt.charCount', { count: formData.seoDescription.length, max: 160 })}</p>
                </div>

                <div>
                  <Label className="text-sm font-medium">{t('provider.treatmentMgmt.seoKeywords')}</Label>
                  <p className="text-xs text-gray-500 mb-1">{t('provider.treatmentMgmt.seoKeywordsHelp')}</p>
                  <Input
                    value={formData.seoKeywords}
                    onChange={(e) => setFormData(prev => ({ ...prev, seoKeywords: e.target.value }))}
                    placeholder={t('provider.treatmentMgmt.seoKeywordsPh')}
                    data-testid="seo-keywords-input"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium">{t('provider.treatmentMgmt.canonicalUrl')}</Label>
                  <p className="text-xs text-gray-500 mb-1">{t('provider.treatmentMgmt.canonicalUrlHelp')}</p>
                  <Input
                    value={formData.canonicalUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, canonicalUrl: e.target.value }))}
                    placeholder={`https://treatx.health/treatments/${formData.slug || 'treatment-slug'}`}
                    data-testid="seo-canonical-input"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium">{t('provider.treatmentMgmt.seoImageLabel')}</Label>
                  <p className="text-xs text-gray-500 mb-1">{t('provider.treatmentMgmt.ogImageHelp')}</p>
                  <Input
                    value={formData.seoImage}
                    onChange={(e) => setFormData(prev => ({ ...prev, seoImage: e.target.value }))}
                    placeholder={formData.coverImage || 'https://images.unsplash.com/...'}
                    data-testid="seo-image-input"
                  />
                  {(formData.seoImage || formData.coverImage) && (
                    <div className="mt-2 w-48 h-28 rounded border overflow-hidden">
                      <img
                        src={formData.seoImage || formData.coverImage}
                        alt={t('provider.treatmentMgmt.seoPreviewAlt')}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Search Preview */}
              <div className="border-t pt-4">
                <Label className="text-base font-semibold mb-3 block">{t('provider.treatmentMgmt.searchResultPreview')}</Label>
                <div className="bg-white border rounded-lg p-4 max-w-xl">
                  <p className="text-blue-800 text-lg hover:underline cursor-pointer truncate">
                    {formData.seoTitle || `${formData.name} | TreatX`}
                  </p>
                  <p className="text-green-700 text-sm truncate">
                    {formData.canonicalUrl || `treatx.health/treatments/${formData.slug || '...'}`}
                  </p>
                  <p className="text-gray-600 text-sm line-clamp-2 mt-1">
                    {formData.seoDescription || formData.shortDescription || t('provider.treatmentMgmt.descAppearsHere')}
                  </p>
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>

        {/* Footer */}
        <div className="p-6 border-t bg-slate-50 flex gap-3 flex-shrink-0">
          <Button variant="outline" onClick={onClose} className="flex-1">
            {t('provider.treatmentMgmt.cancel')}
          </Button>
          <Button 
            onClick={handleSubmit}
            className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600"
          >
            <Save className="h-4 w-4 mr-2" />
            {treatment ? t('provider.treatmentMgmt.updateTreatmentBtn') : t('provider.treatmentMgmt.createTreatmentBtn')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Component
const TreatmentManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { admin, isAuthenticated, logoutAdmin } = useTreatXAdminAuth();
  const {
    getAllTreatments,
    addTreatment,
    updateTreatment,
    deleteTreatment,
    getPriceRange,
    TREATMENT_CATEGORIES,
    taxonomyCategories,
  } = useTreatmentManagement();

  // Build category options from backend taxonomy (source of truth)
  const categoryOptions = taxonomyCategories.length > 0
    ? taxonomyCategories.reduce((acc, cat) => {
        acc[cat.slug.toUpperCase()] = { id: cat.slug, label: getLocalizedField(cat.name, 'en') };
        return acc;
      }, {})
    : TREATMENT_CATEGORIES;

  const [searchQuery, setSearchQuery] = useState('');
  const [editModal, setEditModal] = useState({ open: false, treatment: null });
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/treatx-admin/login');
    }
  }, [isAuthenticated, navigate]);

  if (!admin) return null;

  const allTreatments = getAllTreatments();

  // Filter treatments
  const filteredTreatments = allTreatments.filter(t => {
    const matchesSearch = getLocalizedField(t.name, 'en').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          getLocalizedField(t.shortDescription || t.description?.overview || '', 'en').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || 
                          (statusFilter === 'published' && t.isActive) ||
                          (statusFilter === 'draft' && !t.isActive);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const handleSaveTreatment = (data) => {
    if (editModal.treatment) {
      updateTreatment(editModal.treatment.id, data);
      toast({ title: t('provider.treatmentMgmt.toastUpdated'), description: t('provider.treatmentMgmt.hasBeenUpdated', { name: getLocalizedField(data.name, 'en') }) });
    } else {
      addTreatment({
        ...data,
        calculatedPriceMin: data.priceRange?.min || 1500,
        calculatedPriceMax: data.priceRange?.max || 5000,
        clinicsOffering: Math.floor(Math.random() * 10) + 3
      });
      toast({ title: t('provider.treatmentMgmt.toastCreated'), description: t('provider.treatmentMgmt.hasBeenAdded', { name: getLocalizedField(data.name, 'en') }) });
    }
    setEditModal({ open: false, treatment: null });
  };

  const handleDeleteTreatment = (treatment) => {
    if (window.confirm(t('provider.treatmentMgmt.deactivateConfirm', { name: getLocalizedField(treatment.name, 'en') }))) {
      deleteTreatment(treatment.id);
      toast({ title: t('provider.treatmentMgmt.toastDeactivated'), description: t('provider.treatmentMgmt.hasBeenMovedToDrafts', { name: getLocalizedField(treatment.name, 'en') }) });
    }
  };

  const handleLogout = () => {
    logoutAdmin();
    navigate('/treatx-admin/login');
  };

  // Count content completeness
  const getContentScore = (treatment) => {
    let score = 0;
    if (treatment.description?.overview) score++;
    if (treatment.description?.benefits?.length > 0) score++;
    if (treatment.description?.risks?.length > 0) score++;
    if (treatment.description?.expectations) score++;
    if (treatment.whoIsThisFor?.length > 0) score++;
    if (treatment.whyAbroad?.length > 0) score++;
    if (treatment.faqs?.length > 0) score++;
    return score;
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Toaster />

      {/* Edit Modal */}
      {editModal.open && (
        <TreatmentEditModal
          treatment={editModal.treatment}
          onSave={handleSaveTreatment}
          onClose={() => setEditModal({ open: false, treatment: null })}
          categories={categoryOptions}
        />
      )}

      {/* Header */}
      <header className="bg-gradient-to-r from-purple-800 to-pink-900 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/treatx-admin/dashboard')}
                className="text-purple-200 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('provider.treatmentMgmt.dashboard')}
              </Button>
              <div className="h-6 w-px bg-white/30" />
              <div>
                <h1 className="text-xl font-bold">{t('provider.treatmentMgmt.pageTitle')}</h1>
                <p className="text-sm text-purple-200">{t('provider.treatmentMgmt.pageSubtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <img 
                  src={admin.avatar} 
                  alt={admin.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <span className="text-sm hidden sm:block">{admin.name}</span>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-purple-200 hover:text-white hover:bg-white/10"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{allTreatments.length}</p>
              <p className="text-xs text-slate-500">{t('provider.treatmentMgmt.statTotal')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{allTreatments.filter(t => t.isActive).length}</p>
              <p className="text-xs text-slate-500">{t('provider.treatmentMgmt.statPublished')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600">{allTreatments.filter(t => !t.isActive).length}</p>
              <p className="text-xs text-slate-500">{t('provider.treatmentMgmt.statDrafts')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{Object.keys(categoryOptions).length}</p>
              <p className="text-xs text-slate-500">{t('provider.treatmentMgmt.statCategories')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('provider.treatmentMgmt.catalogTitle')}</CardTitle>
                <CardDescription>{t('provider.treatmentMgmt.catalogSubtitle')}</CardDescription>
              </div>
              <Button 
                onClick={() => setEditModal({ open: true, treatment: null })}
                className="bg-gradient-to-r from-purple-600 to-pink-600"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('provider.treatmentMgmt.addNewTreatment')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('provider.treatmentMgmt.searchPh')}
                  className="pl-9"
                />
              </div>
              <select
                className="border rounded-lg px-3 min-w-[150px]"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">{t('provider.treatmentMgmt.allCategories')}</option>
                {Object.entries(categoryOptions).map(([key, cat]) => (
                  <option key={key} value={cat.id}>{cat.label}</option>
                ))}
              </select>
              <select
                className="border rounded-lg px-3 min-w-[120px]"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">{t('provider.treatmentMgmt.allStatus')}</option>
                <option value="published">{t('provider.treatmentMgmt.published')}</option>
                <option value="draft">{t('provider.treatmentMgmt.draft')}</option>
              </select>
            </div>

            {/* Treatments Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-3 px-4 font-medium text-slate-600">{t('provider.treatmentMgmt.colTreatment')}</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">{t('provider.treatmentMgmt.colCategory')}</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">{t('provider.treatmentMgmt.colPriceRange')}</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">{t('provider.treatmentMgmt.colContent')}</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">{t('provider.treatmentMgmt.colStatus')}</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">{t('provider.treatmentMgmt.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTreatments.map(treatment => {
                    const priceRange = getPriceRange(treatment);
                    const category = categoryOptions[treatment.category?.toUpperCase()];
                    const contentScore = getContentScore(treatment);
                    
                    return (
                      <tr key={treatment.id} className="border-b hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={treatment.image}
                              alt={getLocalizedField(treatment.name, 'en')}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                            <div>
                              <p className="font-medium">{getLocalizedField(treatment.name, 'en')}</p>
                              <p className="text-xs text-slate-500 truncate max-w-[200px]">
                                {getLocalizedField(treatment.shortDescription, 'en') || (getLocalizedField(treatment.description?.overview, 'en') || '').substring(0, 60) + '...'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">
                            {category?.icon} {category?.label || treatment.category}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <span className="font-medium text-green-600">
                              €{priceRange.min.toLocaleString()} - €{priceRange.max.toLocaleString()}
                            </span>
                            <Badge className={`ml-2 ${treatment.priceMode === 'auto' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-amber-100 text-amber-700'
                            }`}>
                              {treatment.priceMode === 'manual' ? t('provider.treatmentMgmt.overrideBadge') : t('provider.treatmentMgmt.autoBadge')}
                            </Badge>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  contentScore >= 6 ? 'bg-green-500' : 
                                  contentScore >= 4 ? 'bg-amber-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${(contentScore / 7) * 100}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-500">{contentScore}/7</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {treatment.isActive ? (
                            <Badge className="bg-green-100 text-green-700">
                              <Eye className="h-3 w-3 mr-1" />
                              {t('provider.treatmentMgmt.published')}
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-500">
                              <EyeOff className="h-3 w-3 mr-1" />
                              {t('provider.treatmentMgmt.draft')}
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditModal({ open: true, treatment })}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteTreatment(treatment)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredTreatments.length === 0 && (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">{t('provider.treatmentMgmt.noTreatmentsFound')}</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setEditModal({ open: true, treatment: null })}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('provider.treatmentMgmt.addFirstTreatment')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default TreatmentManagement;
